import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { PlusIcon, PencilIcon, TrashIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { useModal } from '../hooks/useModal';
import { useCrud } from '../hooks/useCrud';
import { useAsideContext } from '@contexts/AsideContext';
import { PageMeta, Card } from '@common/components';
import { Button, Badge, ConfirmDialog, Input, Select } from '@common/components/ui';
import categoryService from '@services/categoryService';
import { Category } from '@services/core/types';

interface CategoryFormData {
  name_en: string;
  name_tr: string;
  name_sq: string;
  name_bs: string;
  slug: string;
  parentId: string;
  sidebarOrder: number;
  heroOrder: number;
  externalId: string;
}

interface CategoryFormProps {
  category: Category | null;
  parents: Category[];
  onSave: (data: CategoryFormData) => Promise<void>;
  isSaving: boolean;
}

const CategoryForm = ({ category, parents, onSave, isSaving }: CategoryFormProps) => {
  const [formData, setFormData] = useState<CategoryFormData>({
    name_en: category?.name_en || '',
    name_tr: category?.name_tr || '',
    name_sq: category?.name_sq || '',
    name_bs: category?.name_bs || '',
    slug: category?.slug || '',
    parentId: category?.parentId || '',
    sidebarOrder: category?.sidebarOrder ?? 0,
    heroOrder: category?.heroOrder ?? 0,
    externalId: category?.externalId || '',
  });

  const handleNameEnChange = (value: string) => {
    setFormData((prev) => {
      const next = { ...prev, name_en: value };
      // Auto-generate slug from English name, only on create
      if (!category) {
        next.slug = value
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-|-$/g, '');
      }
      return next;
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };

  // Filter out the current category from parent options to prevent circular reference
  const availableParents = parents.filter((p) => p.id !== category?.id);

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Category Names</h3>
        <div className="space-y-4">
          <Input label="English Name" required value={formData.name_en} onChange={(e) => handleNameEnChange(e.target.value)} />
          <Input
            label="Turkish Name"
            value={formData.name_tr}
            onChange={(e) => setFormData((prev) => ({ ...prev, name_tr: e.target.value }))}
          />
          <Input
            label="Albanian Name"
            value={formData.name_sq}
            onChange={(e) => setFormData((prev) => ({ ...prev, name_sq: e.target.value }))}
          />
          <Input
            label="Bosnian Name"
            value={formData.name_bs}
            onChange={(e) => setFormData((prev) => ({ ...prev, name_bs: e.target.value }))}
          />
        </div>
      </div>

      <Input
        label="Slug"
        required
        value={formData.slug}
        onChange={(e) => setFormData((prev) => ({ ...prev, slug: e.target.value }))}
      />

      <Input
        label="External ID"
        value={formData.externalId}
        onChange={(e) => setFormData((prev) => ({ ...prev, externalId: e.target.value }))}
        placeholder="External system category ID (optional)"
        helperText="Used to match products from external sources (e.g., barcode scanner)"
      />

      <Select
        label="Parent Category"
        value={formData.parentId}
        onChange={(e) => setFormData((prev) => ({ ...prev, parentId: e.target.value }))}
        options={[
          { value: '', label: 'None (Top Level)' },
          ...availableParents.map((parent) => ({ value: parent.id, label: parent.name_en })),
        ]}
      />

      <div>
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Display Order</h3>
        <div className="grid grid-cols-2 gap-4">
          <Input
            label="Sidebar Order"
            type="number"
            value={formData.sidebarOrder}
            onChange={(e) => setFormData((prev) => ({ ...prev, sidebarOrder: Number(e.target.value) }))}
          />
          <Input
            label="Hero Order"
            type="number"
            value={formData.heroOrder}
            onChange={(e) => setFormData((prev) => ({ ...prev, heroOrder: Number(e.target.value) }))}
          />
        </div>
      </div>

      <div className="flex gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
        <Button type="submit" loading={isSaving} fullWidth>
          {category ? 'Update Category' : 'Create Category'}
        </Button>
      </div>
    </form>
  );
};

const Categories = () => {
  const queryClient = useQueryClient();
  const { openAside } = useAsideContext();
  const { isOpen: isDeleteOpen, open: openDelete, close: closeDelete } = useModal();

  const [categoryToDelete, setCategoryToDelete] = useState<Category | null>(null);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());

  const { handleCreate, handleEdit, handleDelete, isSubmitting } = useCrud<CategoryFormData, Category>({
    queryKey: 'categories-tree',
    service: {
      create: (data) => categoryService.create(data),
      update: (id, data) => categoryService.update(String(id), data),
      remove: (id) => categoryService.remove(String(id)),
    },
    messages: {
      created: 'Category created successfully',
      updated: 'Category updated successfully',
      deleted: 'Category deleted successfully',
    },
  });

  const invalidateParents = () => queryClient.invalidateQueries({ queryKey: ['categories-parents'] });

  // Fetch category tree
  const { data: treeData, isLoading } = useQuery({
    queryKey: ['categories-tree'],
    queryFn: () => categoryService.getTree(),
  });

  // Fetch parent categories for dropdown
  const { data: parentsData } = useQuery({
    queryKey: ['categories-parents'],
    queryFn: () => categoryService.getParents(),
  });

  const openCategoryForm = (category: Category | null) => {
    openAside(
      <CategoryForm
        category={category}
        parents={parentsData?.data || []}
        isSaving={isSubmitting}
        onSave={async (data) => {
          if (category) {
            await handleEdit(category.id, data);
          } else {
            await handleCreate(data);
          }
          invalidateParents();
        }}
      />,
      { title: category ? 'Edit Category' : 'Add Category' }
    );
  };

  const handleDeleteClick = (category: Category) => {
    setCategoryToDelete(category);
    openDelete();
  };

  const toggleExpand = (categoryId: string) => {
    setExpandedCategories((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(categoryId)) {
        newSet.delete(categoryId);
      } else {
        newSet.add(categoryId);
      }
      return newSet;
    });
  };

  const categories = treeData?.data || [];

  const CategoryRow = ({ category, level = 0 }: { category: Category; level?: number }) => {
    const hasChildren = category.children && category.children.length > 0;
    const isExpanded = expandedCategories.has(category.id);

    return (
      <>
        <tr
          className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          onClick={() => openCategoryForm(category)}
        >
          <td className="px-6 py-4" style={{ paddingLeft: `${1.5 + level * 2}rem` }}>
            <div className="flex items-center gap-2">
              {hasChildren && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleExpand(category.id);
                  }}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <ChevronRightIcon className={`h-4 w-4 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                </button>
              )}
              <span className="font-medium text-gray-900 dark:text-white">{category.name_en}</span>
              {hasChildren && <Badge color="gray">{category.children?.length}</Badge>}
            </div>
          </td>
          <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">{category.slug}</td>
          <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">{category.sidebarOrder ?? '-'}</td>
          <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">{category.heroOrder ?? '-'}</td>
          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => openCategoryForm(category)}
                className="text-brand-600 hover:text-brand-900 dark:text-brand-400 dark:hover:text-brand-300"
              >
                <PencilIcon className="h-5 w-5" />
              </button>
              <button
                onClick={() => handleDeleteClick(category)}
                className="text-danger-600 hover:text-danger-900 dark:text-danger-400 dark:hover:text-danger-300"
              >
                <TrashIcon className="h-5 w-5" />
              </button>
            </div>
          </td>
        </tr>
        {hasChildren && isExpanded && category.children?.map((child) => <CategoryRow key={child.id} category={child} level={level + 1} />)}
      </>
    );
  };

  return (
    <>
      <PageMeta
        title="Categories"
        breadcrumbs={[{ label: 'Categories' }]}
        action={
          <Button onClick={() => openCategoryForm(null)} icon={<PlusIcon className="h-5 w-5" />}>
            Add Category
          </Button>
        }
      />

      <Card padded={false}>
        {isLoading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600 mx-auto"></div>
          </div>
        ) : categories.length === 0 ? (
          <div className="p-8 text-center text-gray-500 dark:text-gray-400">No categories found</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Slug</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Sidebar Order</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Hero Order</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {categories.map((category: Category) => (
                  <CategoryRow key={category.id} category={category} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Delete Confirmation */}
      <ConfirmDialog
        isOpen={isDeleteOpen}
        onClose={() => {
          closeDelete();
          setCategoryToDelete(null);
        }}
        onConfirm={async () => {
          if (!categoryToDelete) return;
          await handleDelete(categoryToDelete.id);
          invalidateParents();
          setCategoryToDelete(null);
        }}
        title="Delete Category"
        description={
          <>
            Are you sure you want to delete category "{categoryToDelete?.name_en}"?
            {categoryToDelete?.children && categoryToDelete.children.length > 0 && (
              <span className="block mt-2 text-danger-600 dark:text-danger-400">
                Warning: This category has {categoryToDelete.children.length} subcategories that will also be affected.
              </span>
            )}
          </>
        }
        confirmLabel="Delete"
        successMessage="Category deleted"
      />
    </>
  );
};

export default Categories;
